import { useState, useRef, useEffect, useContext } from "react";
import { Settings2, Tags, Check, CalendarDays } from "lucide-react";
import { useTranslation } from "../../i18n/i18n";
import { UserPreferencesContext } from "../../contexts/UserPreferencesContext";
import "./GroupModeMenu.css";

const GROUP_MODES = {
	DAY: "day",
	CATEGORY: "category",
};

const GroupModeMenu = () => {
	const { groupMode, setGroupMode, design } = useContext(
		UserPreferencesContext
	);
	const { translate } = useTranslation();

	const [rightOffset, setRightOffset] = useState(8);
	const [open, setOpen] = useState(false); // Determines if rendered
	const [visible, setVisible] = useState(false); // Controls opacity/scale

	const wrapperRef = useRef();
	const menuRef = useRef();

	// Update right offset for responsive positioning
	useEffect(() => {
		const updateOffset = () => {
			const content = document.querySelector(".page-content");
			if (content) {
				const rect = content.getBoundingClientRect();
				const rightPadding = 18; // Optional inner padding to adapt
				const offset = window.innerWidth - (rect.x + rect.width) + rightPadding;
				setRightOffset(offset);
			}
		};

		updateOffset();
		window.addEventListener("resize", updateOffset);
		return () => window.removeEventListener("resize", updateOffset);
	}, []);

	// Outside click handler
	useEffect(() => {
		const handleClickOutside = e => {
			if (
				menuRef.current &&
				!menuRef.current.contains(e.target) &&
				!wrapperRef.current.contains(e.target)
			) {
				closeMenu();
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const toggleMenu = () => {
		if (open) {
			setVisible(false); // start fade out
			setTimeout(() => setOpen(false), 300); // remove from DOM after animation
		} else {
			setOpen(true); // show in DOM
			setTimeout(() => setVisible(true), 10); // allow transition to trigger
		}
	};

	const closeMenu = () => {
		setVisible(false);
		setTimeout(() => setOpen(false), 300); // cleanup after transition
	};

	const options = [
		{
			value: GROUP_MODES.DAY,
			label: translate("history.groupByDay"),
			icon: <CalendarDays size={18} />,
		},
		{
			value: GROUP_MODES.CATEGORY,
			label: translate("history.groupByCategory"),
			icon: <Tags size={18} />,
		},
	];

	return (
		<div
			className="toggle-group-mode-wrapper"
			style={{ right: `${rightOffset}px` }}
			ref={wrapperRef}
		>
			<button
				className={`toggle-group-mode-btn ${
					design === "ios26" ? "glass-effect" : ""
				}`}
				onClick={toggleMenu}
				aria-label={translate("history.groupModeToggle")}
				aria-haspopup="listbox"
			>
				<Settings2 size={18} />
			</button>

			{open && (
				<div
					ref={menuRef}
					className={`group-mode-popover glass-effect lighter-glass-effect ${
						visible ? "visible" : ""
					}`}
				>
					{options.map(opt => (
						<div
							key={opt.value}
							className={`group-mode-option ${
								groupMode === opt.value ? "active" : ""
							}`}
							onClick={() => {
								setGroupMode(opt.value);
								closeMenu();
							}}
						>
							<Check size={15} className="check-icon" />
							<span className="option-label">{opt.label}</span>
							<span className="option-icon">{opt.icon}</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default GroupModeMenu;
